import React, { useState } from "react";
import { CommonButton, CommonModal, CommonStepIndicator } from "../Input";

// Bulk Sub-components
import Step1CustomerSelection from "./CreateSingleTransaction/Step1CustomerSelection";
import Step1ReceiverList from "./SendBulkTransaction/Step1ReceiverList";
import Step2BulkPaymentInfo from "./SendBulkTransaction/Step2BulkPaymentInfo";
import Step3BulkConfirm from "./SendBulkTransaction/Step3BulkConfirm";
import Step4Denomination from "./SendBulkTransaction/Step4Denomination";
import Step5Success from "../Transactions/SendTransaction/Step5Success";
import CreateBeneficiaryModal from "../Beneficiaries/CreateBeneficiaryModal";
import SelectTemplateModal from "./SendBulkTransaction/SelectTemplateModal";

const SendBulkTransactionModal = ({
  show,
  onHide,
  customer: initialCustomer,
  hideStep0 = false,
}) => {
  const [currentStep, setCurrentStep] = useState(hideStep0 ? 2 : 1);
  const [selectedReceivers, setSelectedReceivers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(initialCustomer);
  const [showCreateBeneficiaryModal, setShowCreateBeneficiaryModal] =
    useState(false);
  const [showSelectTemplateModal, setShowSelectTemplateModal] = useState(false);

  const handleToggleReceiver = (item) => {
    if (selectedReceivers.some((r) => r.id === item.id)) {
      setSelectedReceivers(selectedReceivers.filter((r) => r.id !== item.id));
    } else {
      setSelectedReceivers([...selectedReceivers, item]);
    }
  };

  const handleRemoveReceiver = (id) => {
    setSelectedReceivers(selectedReceivers.filter((r) => r.id !== id));
  };

  const handleSelectTemplate = (template) => {
    // In a real app, we'd fetch the receivers for this template
    // For now, let's mock by adding some receivers if the list is empty
    const mockReceivers = [
      {
        id: "8385",
        name: "SPM MYR",
        contactNo: "--",
        receiverType: "Individual",
        relationship: "Customer",
        bank: "RHB ISLAMIC BANK BERHAD",
        correspondenceBank: "--",
        deliveryMethod: "Bank Transfer",
        createdDate: "09/03/2026 16:49:07",
        status: "--",
      },
      {
        id: "8384",
        name: "SparkMinds personal",
        contactNo: "--",
        receiverType: "Individual",
        relationship: "Customer",
        bank: "RHB BANK BERHAD",
        correspondenceBank: "--",
        deliveryMethod: "Bank Transfer",
        createdDate: "09/03/2026 16:47:43",
        status: "--",
      },
    ];
    setSelectedReceivers(mockReceivers);
    setShowSelectTemplateModal(false);
  };

  const handleNext = () => {
    if (currentStep < 6) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > (hideStep0 ? 2 : 1)) setCurrentStep(currentStep - 1);
  };

  const handleClose = () => {
    setCurrentStep(hideStep0 ? 2 : 1);
    setSelectedReceivers([]);
    setSelectedCustomer(initialCustomer);
    onHide();
  };

  const steps = [
    { id: 1, label: "Customer Selection" },
    { id: 2, label: "Receiver List" },
    { id: 3, label: "Payment Information" },
    { id: 4, label: "Confirm Transaction" },
    { id: 5, label: "Transaction Denomination" },
    { id: 6, label: "Transaction Receipt" },
  ];

  const displaySteps = hideStep0
    ? steps.filter((s) => s.id !== 1).map((s) => s.label)
    : steps.map((s) => s.label);
  const displayCurrentStep = hideStep0 ? currentStep - 1 : currentStep;

  return (
    <CommonModal
      show={show}
      onHide={handleClose}
      fullscreen={true}
      backdrop="static"
      headerClassName="bg-light-gray-soft"
      bodyClassName="bg-light-gray-soft"
      footerClassName="bg-light-gray-soft pb-4 pt-0"
      title={
        currentStep < 6 ? (
          <div className="w-100 pe-5">
            <CommonStepIndicator
              steps={displaySteps}
              currentStep={displayCurrentStep}
              width={750}
            />
          </div>
        ) : null
      }
      footer={
        currentStep < 6 ? (
          <div className="d-flex justify-content-end gap-3 w-100 px-4 mt-2">
            {currentStep === 2 && (
              <CommonButton
                variant="outline"
                className="px-4 border-light-gray bg-white text-black"
                style={{ borderColor: "#ddd" }}
                onClick={() => setShowSelectTemplateModal(true)}
              >
                Select Template
              </CommonButton>
            )}
            {currentStep === 2 && (
              <CommonButton
                variant="outline"
                className="px-5 border-light-gray bg-white text-black"
                style={{ borderColor: "#ddd" }}
                onClick={() => setShowCreateBeneficiaryModal(true)}
              >
                Add
              </CommonButton>
            )}
            <CommonButton
              variant="outline"
              className="px-5 bg-white text-black"
              style={{ borderColor: "#ddd" }}
              onClick={
                currentStep === (hideStep0 ? 2 : 1) ? handleClose : handleBack
              }
            >
              {currentStep === (hideStep0 ? 2 : 1) ? "Close" : "Back"}
            </CommonButton>
            <CommonButton
              className="px-5"
              style={{
                backgroundColor:
                  currentStep === 2 && selectedReceivers.length === 0
                    ? "#94A3B8"
                    : "var(--primary)", // Dark Navy
              }}
              disabled={currentStep === 2 && selectedReceivers.length === 0}
              onClick={handleNext}
            >
              {currentStep === 5 ? "Create Transaction" : "Next"}
            </CommonButton>
          </div>
        ) : null
      }
    >
      <div
        className={
          currentStep === 6
            ? "flex-grow-1 d-flex align-items-center justify-content-center"
            : "px-4"
        }
        style={{ minHeight: "550px" }}
      >
        {currentStep === 1 && (
          <Step1CustomerSelection
            selectedCustomer={selectedCustomer}
            onSelectCustomer={(c) => {
              setSelectedCustomer(c);
              handleNext();
            }}
          />
        )}
        {currentStep === 2 && (
          <Step1ReceiverList
            selectedReceivers={selectedReceivers}
            onToggleReceiver={handleToggleReceiver}
          />
        )}
        {currentStep === 3 && (
          <Step2BulkPaymentInfo
            selectedReceivers={selectedReceivers}
            onRemoveReceiver={handleRemoveReceiver}
          />
        )}
        {currentStep === 4 && (
          <Step3BulkConfirm selectedReceivers={selectedReceivers} />
        )}
        {currentStep === 5 && <Step4Denomination />}
        {currentStep === 6 && <Step5Success onClose={handleClose} />}
      </div>

      <CreateBeneficiaryModal
        show={showCreateBeneficiaryModal}
        onHide={() => setShowCreateBeneficiaryModal(false)}
      />

      <SelectTemplateModal
        show={showSelectTemplateModal}
        onHide={() => setShowSelectTemplateModal(false)}
        onSelect={handleSelectTemplate}
      />
    </CommonModal>
  );
};


export default SendBulkTransactionModal;
